import { useApolloClient } from '@apollo/react-hooks';
import { EventSyncStatus } from 'model/calendar/events';
import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router';
import type { Observable } from 'zen-observable-ts';
import { EVENT_STATUS } from 'utils/constants/status';
import { EVENT_STATUS_MIN_SUCCESS_RANGE } from 'utils/constants/planner';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { SYNC_STATUS_SUBSCRIPTION } from 'api/graphql/v2/subscriptions/Events';
import { isPlannerRoute } from 'helpers/routeHelper';
import { calendarEventsPrefVar } from 'utils/cache/filters';

// Minimal structural type — we only need unsubscribe().
type ZenSubscription = { unsubscribe: () => void };

type ApolloLike = {
  subscribe: (options: { query: any; variables?: any }) => Observable<any>;
};

export const compareStatuses = (
  originalStatus?: number,
  subscriptionStatus?: number
) => {
  if (subscriptionStatus === undefined || subscriptionStatus === null)
    return originalStatus;

  return subscriptionStatus;
};
const isTerminallySynced = (
  status: number | undefined | null
): boolean => {
  if (status === undefined || status === null) return false;
  return status === 0 || status >= EVENT_STATUS_MIN_SUCCESS_RANGE;
};

type SyncStatusPayload = {
  crStatus?: number | null;
  athenaStatus?: number | null;
};
type Listener = (payload: SyncStatusPayload) => void;

interface EventEntry {
  sub: ZenSubscription;
  listeners: Set<Listener>;
  teardownTimer: ReturnType<typeof setTimeout> | null;
}

const registry = new Map<number, EventEntry>();

const subscribeToEvent = (
  client: ApolloLike,
  eventId: number,
  listener: Listener
) => {
  const existing = registry.get(eventId);
  if (existing) {
    if (existing.teardownTimer !== null) {
      clearTimeout(existing.teardownTimer);
      existing.teardownTimer = null;
    }
    existing.listeners.add(listener);
    return;
  }

  const listeners = new Set<Listener>();
  listeners.add(listener);

  console.log(
    '[useEventSyncStatus] starting singleton subscription for eventId:',
    eventId
  );

  const sub = client
    .subscribe({
      query: SYNC_STATUS_SUBSCRIPTION,
      variables: { eventId }
    })
    .subscribe({
      next: ({ data }: { data?: { eventSyncStatus?: SyncStatusPayload } }) => {
        const payload = data?.eventSyncStatus;
        if (!payload) return;
        const entry = registry.get(eventId);
        if (!entry) return;
        entry.listeners.forEach(l => {
          try {
            l(payload);
          } catch (e) {
            console.error('[useEventSyncStatus] listener error:', e);
          }
        });
      },
      error: (err: any) => {
        console.error(
          '[useEventSyncStatus] subscription error for eventId:',
          eventId,
          err
        );
      }
    });

  registry.set(eventId, { sub, listeners, teardownTimer: null });
};

const unsubscribeFromEvent = (eventId: number, listener: Listener) => {
  const entry = registry.get(eventId);
  if (!entry) return;
  entry.listeners.delete(listener);
  if (entry.listeners.size > 0) return;
  if (entry.teardownTimer !== null) return;

  entry.teardownTimer = setTimeout(() => {
    const current = registry.get(eventId);
    if (!current) return;
    if (current.listeners.size > 0) {
      current.teardownTimer = null;
      return;
    }
    console.log(
      '[useEventSyncStatus] tearing down singleton subscription for eventId:',
      eventId
    );
    current.sub.unsubscribe();
    registry.delete(eventId);
  }, 0);
};

export const useEventSyncStatus = (event: EventSyncStatus) => {
  // Solution 1: only listen for per-event sync-status updates on the Planner
  // page. Other tabs (Providers, Provider Details, Clients, etc.) should not
  // register subscriber rows for this.
  const location = useLocation();
  const onPlannerPage = isPlannerRoute(location?.pathname);

  // Initialize with values from the event prop (from database)
  const [syncData, setSyncData] = useState<{
    crStatus?: number;
    athenaStatus?: number;
  }>({
    crStatus: event.crStatus,
    athenaStatus: event.athenaStatus
  });

  // Normalize eventId to number
  const eventIdNum = useMemo(() => {
    if (event.eventId === undefined || event.eventId === null) return undefined;
    const num =
      typeof event.eventId === 'string'
        ? parseInt(event.eventId, 10)
        : event.eventId;
    return isNaN(num) ? undefined : num;
  }, [event.eventId]);

  const featureEnabled = getFeatureAvailability(
    FEATURES.EVENT_SYNC_STATUS_SUBSCRIPTIONS
  );
  const isNotCanceled = event?.status !== EVENT_STATUS.canceled;

  const effectiveCrStatus = syncData.crStatus ?? event.crStatus;
  const effectiveAthenaStatus = syncData.athenaStatus ?? event.athenaStatus;
  const needsLiveUpdates =
    !isTerminallySynced(effectiveCrStatus) ||
    !isTerminallySynced(effectiveAthenaStatus) ||
    // While a cancel is in progress, keep listening even for a terminally-synced
    // event so we receive the Athena cancel's athenaStatus 3→103 pushes and can
    // transition the "In-progress Cancel to Athena" tooltip to "…in Axon".
    !!event.isCancelInProgress;

  const shouldListen =
    !!eventIdNum &&
    isNotCanceled &&
    featureEnabled &&
    onPlannerPage &&
    needsLiveUpdates;

  const client = (useApolloClient() as unknown) as ApolloLike;

  // Update initial values when event prop changes (e.g., calendar refresh)
  useEffect(() => {
    if (event.crStatus !== undefined || event.athenaStatus !== undefined) {
      setSyncData(prev => ({
        crStatus: event.crStatus ?? prev.crStatus,
        athenaStatus: event.athenaStatus ?? prev.athenaStatus
      }));
    }
  }, [event.crStatus, event.athenaStatus]);

  useEffect(() => {
    if (!shouldListen || !eventIdNum) return;

    const listener: Listener = payload => {
      setSyncData(prev => {
        const next = { ...prev };
        if (payload.crStatus !== null && payload.crStatus !== undefined) {
          next.crStatus = payload.crStatus;
        }
        if (payload.athenaStatus !== null && payload.athenaStatus !== undefined) {
          next.athenaStatus = payload.athenaStatus;
        }
        return next;
      });
      const currentEvents = calendarEventsPrefVar();
      let changed = false;
      const nextEvents = currentEvents.map(e => {
        if (e.id !== eventIdNum) return e;
        const newCrStatus =
          payload.crStatus !== null && payload.crStatus !== undefined
            ? payload.crStatus
            : e.crStatus;
        const newAthenaStatus =
          payload.athenaStatus !== null && payload.athenaStatus !== undefined
            ? payload.athenaStatus
            : e.athenaStatus;
        if (newCrStatus === e.crStatus && newAthenaStatus === e.athenaStatus) {
          return e;
        }
        changed = true;
        return { ...e, crStatus: newCrStatus, athenaStatus: newAthenaStatus };
      });
      if (changed) calendarEventsPrefVar(nextEvents);
    };

    subscribeToEvent(client, eventIdNum, listener);
    return () => unsubscribeFromEvent(eventIdNum, listener);
  }, [client, shouldListen, eventIdNum]);

  return {
    eventId: eventIdNum,
    crStatus: syncData.crStatus,
    athenaStatus: syncData.athenaStatus
  };
};
